import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener } from '@angular/core';
import { WindowRef } from "@spartacus/core";
import { Observable, ReplaySubject, Subscription } from "rxjs";
import { GenCustomerService } from "../../services/customer.service";
import { filter, tap } from "rxjs/operators";
import { GenLoadingEnum } from "../../enums/loading.enum";
import { GenShippingAccountsService } from '../../services/shipping-accounts.service';
import { GenShippingAccount, GenShippingAccountCondition } from '../../models/shipping-account.model';

@Component({
    selector: 'gen-shipping-accounts',
    templateUrl: './shipping-accounts.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GenShippingAccountsComponent {
  loading: GenLoadingEnum = GenLoadingEnum.Idle;
  GenLoadingEnum = GenLoadingEnum;
  shippingAccountsColumns: {cssClass: string, fieldName: keyof GenShippingAccount | keyof GenShippingAccountCondition, displayName: string}[] = [{
    cssClass: 'active',
    fieldName: 'active',
    displayName: 'active'
  }, {
    cssClass: 'carrier',
    fieldName: 'vendorName',
    displayName: 'carrier',
  }, {
    cssClass: 'shipping-conditions',
    fieldName: 'shippingConditionName',
    displayName: 'shipping conditions',
  }, {
    cssClass: 'account',
    fieldName: 'accountName',
    displayName: 'account',
  }, {
    cssClass: 'contact-name',
    fieldName: 'contactName',
    displayName: 'contact name',
  }, {
    cssClass: 'contact-phone',
    fieldName: 'contactPhone',
    displayName: 'contact phone',
  }, {
    cssClass: 'contact-address',
    fieldName: 'contactAddress',
    displayName: 'contact address',
  }, {
    cssClass: 'ltl-carrier',
    fieldName: 'ltl',
    displayName: 'ltl carrier',
  }, {
    cssClass: 'default',
    fieldName: 'default',
    displayName: 'default',
  }, {
    cssClass: 'account-no',
    fieldName: 'accountNumber',
    displayName: 'account no.',
  }, {
    cssClass: 'note',
    fieldName: 'contactNotes',
    displayName: 'note',
  }];

  @HostListener('window:resize')
  onResize() {
    this.isMobileView = this.winRef?.nativeWindow.innerWidth < 768;
  }

  isMobileView: boolean;

  private shippingAccountsSubject = new ReplaySubject<GenShippingAccount[]>(1);
  shippingAccounts$: Observable<GenShippingAccount[]> = this.shippingAccountsSubject.asObservable();

  private subscription = new Subscription();

  constructor(
    protected customerService: GenCustomerService,
    protected winRef: WindowRef,
    private cdr: ChangeDetectorRef,
    private shippingAccountService: GenShippingAccountsService,
  ) {
    this.isMobileView = this.winRef?.nativeWindow.innerWidth < 768;
  }

  ngOnInit() {
    this.getShippingAccounts();
    this.subscribeToLoadingState();
    this.subscribeToUnitChange();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  trackByShippingAccounts(_: number, shippingAccount: GenShippingAccount): string {
    return shippingAccount.vendorId;
  }

  getShippingAccountValue(
    shippingAccount: GenShippingAccount,
    shippingCondition: GenShippingAccountCondition,
    fieldName: keyof GenShippingAccount | keyof GenShippingAccountCondition
  ): any {
    if (fieldName in shippingAccount) {
      return shippingAccount[fieldName as keyof GenShippingAccount];
    } else if (fieldName in shippingCondition) {
      return shippingCondition[fieldName as keyof GenShippingAccountCondition];
    }
    return null;
  }

  private getShippingAccounts(): void {
    this.subscription.add(
      this.shippingAccountService.getShippingAccounts().pipe(
        tap((shippingAccounts: GenShippingAccount[]) => {
          this.shippingAccountsSubject.next(shippingAccounts);
        })
      ).subscribe()
    )
  }

  private subscribeToUnitChange(): void {
    this.subscription.add(
      this.customerService.getB2bUnitChanged().pipe(
        filter((isB2bUnitChanged) => !!isB2bUnitChanged),
        tap(() => this.getShippingAccounts())
      ).subscribe()
    );
  }

  private subscribeToLoadingState() {
    this.subscription.add(
      this.shippingAccountService.loading$
        .subscribe(loading => {
          this.loading = loading;
          this.cdr.markForCheck();
        })
    );
  }
}
